/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useDropzone } from "react-dropzone";
import {
	FormControl,
	FormHelperText,
	InputLabel,
	ImageList,
	ImageListItem,
	ImageListItemBar,
	IconButton,
	Paper,
	makeStyles,
} from "@material-ui/core";
import Icon from "../../presentation/Icon";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import getUploadedFileOriginalName from "../../../lib/utils/getUploadedFileOriginalName";
import AutoDataGrid from "../../grid/functional/AutoDataGrid";
import { getOption } from "../../../lib/api/getOptions";

const useStyles = makeStyles((theme) => ({
	container: {
		marginTop: "16px",
	},
	image: {
		border: "solid 0.5px gray",
	},
	imageList: {
		width: "100%",
	},
	imageListItem: {
		height: "auto",
	},
	fileName: {
		border: "solid 0.5px gray",
		height: "100%",
	},
	fileNameText: {
		position: "absolute",
		top: "50%",
		left: "50%",
		width: "100%",
		textAlign: "center",
		transform: "translate(-50%,-50%)",
		fontSize: "large",
	},
	closeIcon: {
		color: "rgba(255, 255, 255, 0.54)",
	},
}));

function getFileExtension(file) {
	let extension =
		(file && file.split(".")[file.split(".").length - 1]) || file;

	return extension;
}

function urlToFileName(url) {
	if (!url) {
		return "";
	}

	let fileName = url.substring(url.lastIndexOf("/") + 1);

	return fileName;
}

function isFileImage(file) {
	const acceptedImageTypes = ["image/gif", "image/jpeg", "image/png"];

	return file && acceptedImageTypes.includes(file["type"]);
}

function isFileTypeAllowed(file, allowedFileTypes) {
	return file && allowedFileTypes.includes(file["type"]);
}

function isUrlImage(url) {
	const acceptedImageTypes = ["gif", "jpeg", "jpg", "png"];

	if (!url) {
		return false;
	}

	let fileName = urlToFileName(url);
	let extension = getFileExtension(fileName);

	return url && acceptedImageTypes.includes(extension.toLowerCase());
}

/**
 * @COMPONENT_NAME FormItemFile
 * @DESCRIPTION getOption("fileUpload", "logHistory")`가 true일 경우 파일 변경 이력을 기록할 수 있도록 DataGrid를 렌더링합니다.
 *  			업로드된 파일의 추가/삭제/수정 작업에 대한 메타데이터(`operation`, `description`, `file`)를 기록합니다.
 * 				파일에 관한 meta데이터(operation, description, file)를 저장할 deletedFilesMeta도 setFiles에 추가하며 activeFiles 내에도 meta데이터를 추가로 저장합니다.
 * 				이 메타데이터는 `onChange`를 통해 상위 컴포넌트로 전달됩니다.
 *  			이미지 파일은 미리보기를 제공하며, 비이미지 파일은 확장자명을 표시합니다.
 *
 * @PROPS
 *   - {string} id - 폼 아이디 및 레이블 연결용 식별자
 *   - {Object} column - 스키마 컬럼 정보 (maxFiles, allowedFileTypes 등 포함)
 *   - {Object} value - 초기 파일 데이터 구조 (activeFiles, deletedFiles, deletedFilesMeta 포함)
 *   - {Object} error - 에러 상태 여부
 *   - {string} helperText - 헬퍼 텍스트
 *   - {Object} InputLabelProps - MUI InputLabel에 전달할 props
 *   - {Function} onChange - 파일 목록이나 메타데이터가 변경될 때 호출되는 콜백
 *   - {Object} others - 기타 props
 * @VALUE_STRUCTURE
 *   - value = {
 *      activeFiles: Array<{ type: "file"|"url", file?, url?, operation?, description? }>,
 *      deletedFiles: Array<string>, // URL 목록
 *      deletedFilesMeta: Array<{ url: string, operation: string, description: string }>
 *     }
 *
 * @PASSTHROUGH_PROPS
 *   - {Array} rows - setRows를 통해 설정된 grid에 관한 배열을 AutoDataGrid의 itemsSource에 전달
 *   - {Object} fileSchema - fileSchema는 grid의 schema정보이며 useMemo로 감싸서 리렌더링 방지
 *   - {Boolean} isReadOnly - grid 수정옵션이며 false
 *   - {Function} onInitialized - grid 셀 변경 시 해당 콜백함수 호출되며 내부에서 onChange를 호출하여 변경된 description 내용을 상위 컴포넌트에 전달
 * @CALLBACKS
 *	  - {Function} onDrop(acceptedFiles) - 사용자가 파일을 드롭 또는 선택했을 때 호출됨
 *	  - {Function} onRemoveFile(event, index) - 특정 인덱스의 파일을 제거할 때 호출됨
 * @BEHAVIOR
 *  - 드래그 앤 드롭 또는 클릭을 통해 파일을 업로드할 수 있음
 *  - 같은 이름의 파일이 기존에 있던 경우 `operation: "update"`로 간주
 *  - 삭제 시 `operation: "delete"`가 자동 설정되며 activeFiles에 있었던 데이터와 파일이름이 같고 logHistory가 true인 경우 DataGrid에 기록됨
 */

function FormItemFile(props) {
	const { t } = useTranslation();
	
	const {
		id,
		column,
		value,
		error,
		helperText,
		InputLabelProps,
		onChange,
		...others
	} = props;

	const classes = useStyles();
	const [rows, setRows] = useState([]);
	const [files, setFiles] = useState(
		value
			? value
			: {
					deletedFiles: [],
					activeFiles: [],
					deletedFilesMeta: []
			  }
	);

	const [logHistory, setLogHistory] = useState(false);

	const initialFilesRef = useRef({
		activeFiles: value?.activeFiles || [],
	});

	const fileSchema = useMemo(() => ({
	name: "FileUploadTable",
	columns: [
		{
			name: "fileName",
			displayName: t("filename"),
			dataType: "String",
			grid: { width: 250 },
		},
		{
			name: "extension",
			displayName: t("extension"),
			dataType: "String",
			grid: { width: 100 },
		},
		{
			name: "operation",
			displayName: t("operation"),
			dataType: "String",
			grid: {
				type: "select",
				width: 120,
			},
			selectItems: [
				{ name: "추가", value: "add" },
 				{ name: "삭제", value: "delete" },
 				{ name: "변경", value: "update" },
			],
		},
		{
			name: "description",
			displayName: t("description"),
			dataType: "String",
			grid: { width: 300 },
		},
	],
}), []);

	useEffect(() => {
		let mounted = true;

		(async () => {
			let logHistoryBoolean = await getOption("fileUpload", "logHistory");

			if (mounted) {
				if (logHistoryBoolean?.toLowerCase() === "true") {
					setLogHistory(true);
				} else {
					setLogHistory(false);
				}
			}
		})();

		return () => {
			mounted = false;
		};
	}, []);

	const onDrop = useCallback(
		
		(acceptedFiles) => {
			let currentCount = 0;
			setFiles((files) => {
				let newFiles = files
					? cloneDeep(files)
					: {
							deletedFiles: [],
							activeFiles: [],
							deletedFilesMeta: []
					  };
				currentCount = newFiles.activeFiles.length;
				for (const file of acceptedFiles) {
					if (
						column.maxFiles > 0 &&
						newFiles.activeFiles.length === column.maxFiles
					) {
						alert(t("infoMsg.noMoreFileAllowed"));
						break;
					}

					if (
						column.allowedFileTypes &&
						!isFileTypeAllowed(file, column.allowedFileTypes)
					) {
						alert(t("error.notAllowedFileType"));
						break;
					}

					if (
						newFiles.activeFiles.find((prev) => {
							if (prev.type === "url") {
								if (
									getUploadedFileOriginalName(
										urlToFileName(prev.url)
									).localeCompare(file.path) === 0
								) {
									return true;
								}
							}

							if (prev.type === "file") {
								if (prev.file.path === file.path) {
									return true;
								}
							}

							return false;
						})
					)
						continue;

					if(initialFilesRef.current.activeFiles.some((prevFile) => {
						const prevName =
							prevFile?.type === "url"
								? getUploadedFileOriginalName(
										urlToFileName(prevFile?.url)
								  )
								: prevFile?.file?.name;
						return prevName === file?.name;
					})) {
						
						const targetName = file.name;
						newFiles.deletedFilesMeta = newFiles.deletedFilesMeta?.filter((entry) => {
							const fileName = getUploadedFileOriginalName(
										urlToFileName(entry?.url)
								  )
							return fileName !== targetName
						})
						
						newFiles.activeFiles.push({
						type: "file",
						file: file,
						operation: "update",
						description: "",
					});
					}else {
						newFiles.activeFiles.push({
						type: "file",
						file: file,
						operation: "add",
						description: "",
					});
					}
					
				}

				if (onChange) {
					onChange(newFiles);
				}
				return newFiles;
			});

			if (logHistory) {
				setRows((prevRows) => {
					const existingFileNames = new Set([
						...prevRows.map((row) => row.fileName),
						...(files?.activeFiles || []).map((file) =>
							file.type === "url"
								? getUploadedFileOriginalName(
										urlToFileName(file.url)
								  )
								: file.file.name
						),
					]);
					const newRowObjects = [];
					let updatedRows = [...prevRows];
					for (const file of acceptedFiles) {
						const fileName = file.name;
						const extension = getFileExtension(fileName);

						//기존 삭제 row에서 되살리는 경우
						const deleteRowIndex = prevRows.findIndex(
							(row) =>
								row.fileName === fileName &&
								row.extension === extension &&
								row.operation === "delete"
						);
						if (
							column.allowedFileTypes &&
							!isFileTypeAllowed(file, column.allowedFileTypes)
						) {
							break;
						}
					
						if (deleteRowIndex !== -1) {
							updatedRows[deleteRowIndex] = {
							...updatedRows[deleteRowIndex],
							operation: "update",
							description: "",
							};
							continue;
						}

						//신규 행 추가 (중복은 제외)
						if (
							!existingFileNames.has(fileName) && (typeof column.maxFiles !== "number"  || currentCount < column.maxFiles)
							) {
							newRowObjects.push({
								fileName,
								extension,
								operation: "add",
								description: "",
							});
							currentCount++
						}
					}

					return [...newRowObjects, ...updatedRows];
				});
			}
		},
		[logHistory]
	);

	const { getRootProps, getInputProps } = useDropzone({ onDrop });
	const { ...rootProps } = getRootProps();

	const onRemoveFile = (event, index) => {
		setFiles((files) => {
			let newFiles = files
				? cloneDeep(files)
				: {
						deletedFiles: [],
						activeFiles: [],
						deletedFilesMeta: []
				  };

			const removed = newFiles.activeFiles[index];

			if (removed.type === "url") {
				if (logHistory) {
					if (!newFiles.deletedFilesMeta) {
						newFiles.deletedFilesMeta = []; //초기화
					}
					newFiles.deletedFilesMeta.push({
						url: removed.url,
						operation: "delete",
						description: "",
					});
				} 
				
				newFiles.deletedFiles.push(removed.url);
				
			}

			newFiles.activeFiles.splice(index, 1);

			if (logHistory) {
				//기존 value에 포함된 파일만 delete row 추가
				const removedFileName =
					removed.type === "url"
						? getUploadedFileOriginalName(
								urlToFileName(removed.url)
						  )
						: removed.file.name;

				const existedInInitialValue =
					initialFilesRef.current.activeFiles.some((file) => {
						const name =
							file.type === "url"
								? getUploadedFileOriginalName(
										urlToFileName(file.url)
								  )
								: file.file.name;
						return name === removedFileName;
					});

				if (existedInInitialValue) {
					//기존 값에 존재했던 파일이면 삭제 행 추가
					const extension = getFileExtension(removedFileName);

					setRows((prevRows) => {
						const rowIndex = prevRows.findIndex(
							(row) =>
								row.fileName === removedFileName &&
								row.extension === extension &&
								row.operation === "update"
						);

						if (rowIndex !== -1) {
							//기존 update → delete로 덮어쓰기
							const newRows = [...prevRows];
							newRows[rowIndex] = {
								...newRows[rowIndex],
								operation: "delete",
								description: "",
							};
							return newRows;
						}

						//기존에 없던 delete row를 새로 추가
						return [
							...prevRows,
							{
								fileName: removedFileName,
								extension,
								operation: "delete",
								description: "",
							},
						];
					});
				} else {
					//기존 값에 없던 파일이면 해당 추가된 행을 제거
					setRows((prevRows) =>
						prevRows.filter(
							(row) => row.fileName !== removedFileName
						)
					);
				}
			}

			if (onChange) {
				onChange(newFiles);
			}

			return newFiles;
		});

		event.stopPropagation();
	};

	return (
		<FormControl
			error={error}
			required={column.required}
			style={{ width: column.width, height: column.height }}
			{...others}
		>
			<InputLabel id={id + "-label"} shrink={true} {...InputLabelProps}>
				{t(`columnName.${column.displayName || column.name}`)}
			</InputLabel>
			<Paper
				variant="outlined"
				style={{
					height: column && column.height ? column.height : 250,
					display: "flex",
					flexWrap: "wrap",
					justifyContent: "space-around",
					overflow: "auto",
					color: "primary",
					backgroundImage:
						column.maxFiles > 0 &&
						files.activeFiles.length === column.maxFiles
							? `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' version='1.1'><text x='50%' y='50%' fill='gray' font-size='16' text-anchor='middle'>${t(
									"infoMsg.noMoreFileAllowed"
							  )}</text></svg>")`
							: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' version='1.1'><text x='50%' y='50%' fill='gray' font-size='16' text-anchor='middle'>${t(
									"infoMsg.dragAndDropFileOrClickHere"
							  )}</text></svg>")`,
					marginTop: "16px",
					marginBottom: "16px",
					padding: "16px",
				}}
				{...rootProps}
			>
				{
					<ImageList
						cols={(column && column.fileUploadPanelColCount) || 4}
						className={classes.imageList}
						style={{
							height:
								(column && column.height
									? column.height
									: 250) - 32,
						}}
					>
						{files.activeFiles.map((activeFile, index) => (
							<ImageListItem
								key={
									activeFile.type === "url"
										? activeFile.url
										: activeFile.file.name
								}
								className={classes.imageListItem}
							>
								{activeFile.type === "url" ? (
									isUrlImage(
										urlToFileName(activeFile.url)
									) ? (
										<img
											alt={urlToFileName(activeFile.url)}
											className={classes.image}
											src={
												process.env.NODE_ENV ===
													"production" ||
												process.env.NODE_ENV ===
													"staging"
													? activeFile.url
													: "http://localhost:8080" +
													  activeFile.url
											}
										/>
									) : (
										<div className={classes.fileName}>
											<p className={classes.fileNameText}>
												{getFileExtension(
													urlToFileName(
														activeFile.url
													)
												).toUpperCase()}
											</p>
										</div>
									)
								) : isFileImage(activeFile.file) ? (
									<img
										alt={activeFile.file.name}
										className={classes.image}
										src={URL.createObjectURL(
											activeFile.file
										)}
									/>
								) : (
									<div className={classes.fileName}>
										<p className={classes.fileNameText}>
											{getFileExtension(
												activeFile.file.name
											).toUpperCase()}
										</p>
									</div>
								)}
								<ImageListItemBar
									subtitle={
										activeFile.type === "url"
											? getUploadedFileOriginalName(
													urlToFileName(
														activeFile.url
													)
											  )
											: activeFile.file.name
									}
									position="top"
									actionIcon={
										<IconButton
											className={classes.closeIcon}
											sx={{
												color: "rgba(255, 255, 255, 0.54)",
											}}
											onClick={(event) =>
												onRemoveFile(event, index)
											}
										>
											<Icon>close</Icon>
										</IconButton>
									}
									actionPosition="right"
								/>
							</ImageListItem>
						))}
					</ImageList>
				}
			</Paper>
			<input {...getInputProps()} />
			<FormHelperText>{helperText}</FormHelperText>
			{logHistory && (
				<AutoDataGrid
					id="uploadGrid"
					schema={fileSchema}
					isReadOnly={false}
					itemsSource={rows}
					onInitialized={(grid) => {
						// description 외 셀 편집 제한
						grid.beginningEdit.addHandler((grid, e) => {
							const colName = grid.columns[e.col].binding;
							if (colName !== "description") e.cancel = true;
						});
						grid.cellEditEnded.addHandler((grid, e) => {
							if (!logHistory) return; //logHistory가 false면 무시

							const row = grid.rows[e.row].dataItem;
							const {
								fileName,
								extension,
								description,
								operation,
							} = row;

							setFiles((prevFiles) => {
								const updated = cloneDeep(prevFiles);

								//activeFiles에 반영
								updated.activeFiles = updated.activeFiles.map(
									(f) => {
										if (f.type === "url") {
											const name =
												getUploadedFileOriginalName(
													urlToFileName(f.url)
												);
											const ext = getFileExtension(
												urlToFileName(f.url)
											);
											if (
												name === fileName &&
												ext === extension
											) {
												return {
													...f,
													description,
													operation,
												};
											}
										} else if (f.type === "file") {
											const name = f.file.name;
											const ext = getFileExtension(
												f.file.name
											);
											if (
												name === fileName &&
												ext === extension
											) {
												return {
													...f,
													description,
													operation,
												};
											}
										}
										return f;
									}
								);

								updated.deletedFilesMeta = (updated.deletedFilesMeta || []).map(
									(f) => {
										if (typeof f === "object") {
											const name =
												getUploadedFileOriginalName(
													urlToFileName(f.url)
												);
											const ext = getFileExtension(
												urlToFileName(f.url)
											);
											if (
												name === fileName &&
												ext === extension
											) {
												return {
													...f,
													description,
													operation,
												};
											}
										}
										return f;
									}
								);

								const newFiles = {
									...updated
								};

								if (onChange) {
									onChange(newFiles); //description 반영 후 onChange 호출
								}

								return newFiles;
							});
						});
					}}
				/>
			)}
		</FormControl>
	);
}

export default FormItemFile;
